import {
  createArrayWithLengthCallback,
  updateClearState,
  updateRedoState,
  updateUndoState,
} from './toolButtonState';
import { intersection, mesh, object, redoPattern, undoPatterns } from './webGL.js';

const THREE = window.THREE;
// Global variables for style handling
export const undoStyle = createArrayWithLengthCallback([], (newLength, oldLength, array) => {
  updateUndoState(array);
  updateClearState();
});
export const redoStyle = createArrayWithLengthCallback([], (newLength, oldLength, array) => {
  updateRedoState(array);
  updateClearState();
});

window.styleScale = function (scale) {
  let _scale = (scale + 50) / 100;
  if (intersection && intersection.mesh != null) {
    intersection.mesh.material.map.repeat.set(_scale, _scale);
  } else if (mesh) {
    mesh.forEach((m) => {
      if (m.material && m.material.map) {
        m.material.map.repeat.set(_scale, _scale);
      }
    });
  }
};

export function doUndoStyle() {
  if (undoStyle.length > 0) {
    const currentStyle = undoStyle.pop();
    const lastSelectedStyle = undoStyle[undoStyle.length - 1];
    styleHandling?.applyStyleToAllSections(lastSelectedStyle);
    redoStyle.push(currentStyle);
  }
}

export function doRedoStyle() {
  if (redoStyle.length > 0) {
    const lastSelectedStyle = redoStyle.pop();
    styleHandling?.applyStyleToAllSections(lastSelectedStyle);
    undoStyle.push(lastSelectedStyle);
  }
}

export function clearAllStyle() {
  //Apply the first style in style list to all sections
  if (window.styleList && window.styleList.length > 0) {
    const firstStyle = window.styleList[0];
    if (firstStyle && firstStyle.main_texture) {
      styleHandling?.updateUndoStyle(firstStyle);
      styleHandling?.applyStyleToAllSections(firstStyle);
    } else {
      console.warn('First style is invalid or missing main_texture:', firstStyle);
    }
  }
}

class StyleHandling {
  constructor() {
    this._ = undefined;
  }

  updateUndoStyle(style) {
    undoStyle.push(style);
    redoStyle.splice(0, redoStyle.length);
  }

  applyStyleToAllSections(style) {
    if (!style) {
      console.warn('No style provided to applyStyleToAllSections');
      return;
    }

    if (!style.main_texture) {
      console.warn('No main_texture found in style object');
      return;
    }

    // Check if the new style is different from current style
    if (window.currentStyle && style.Id === window.currentStyle.Id) {
      return;
    }

    const textureLoader = new THREE.TextureLoader();
    textureLoader.crossOrigin = 'Anonymous';

    const mainTexturePath = style.main_texture;

    const newTexture = textureLoader.load(
      mainTexturePath,
      () => {},
      undefined,
      (error) => {
        console.error('Error loading main texture:', error);
      },
    );

    newTexture.wrapS = newTexture.wrapT = THREE.RepeatWrapping;
    newTexture.repeat.set(style.Scale || 1, style.Scale || 1);

    // Load the style's normal map once (instead of per-mesh inside the loop).
    const normalTexture = style.normal_texture
      ? textureLoader.load(style.normal_texture)
      : null;

    if (undoPatterns.length > 0) {
      undoPatterns.forEach((pattern) => {
        pattern.currentValue.map = newTexture;
      });
    }
    if (redoPattern.length > 0) {
      redoPattern.forEach((pattern) => {
        pattern.currentValue.map = newTexture;
      });
    }
    if (object) {
      object.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          // Check if this mesh has pattern applied
          const hasPattern = child.patternFile || child.updatedTexture;

          if (hasPattern) {
            // Mesh has pattern: update mapOrigin to new style texture so pattern can be restored to new style later
            child.mapOrigin = newTexture;
            // Keep the current pattern texture, don't overwrite it
          } else {
            // Mesh doesn't have pattern: apply style texture directly
            child.material.map = newTexture;
            // Update mapOrigin for future pattern applications
            child.mapOrigin = newTexture;
          }

          // Only touch the normal map if the style provides one.
          if (normalTexture) {
            if (hasPattern) {
              // Pattern meshes must stay flat: keep the live normal map cleared so
              // the baked ribs don't stripe through the pattern. Stash the style's
              // normal so a later pattern-reset (restoreNormalMapFromOrigin) can
              // bring back the correct look.
              child.normalMapOrigin = normalTexture;
              child.material.normalMap = null;
            } else {
              child.material.normalMap = normalTexture;
            }
          }
          child.material.needsUpdate = true;
        }
      });

      // Update current style after successful application
      window.currentStyle = style;
    } else {
      console.warn('No window.object found to apply style to');
    }
  }
}

export const styleHandling = new StyleHandling();
